import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '@core/api';

export const conversationsApi = createApi({
  reducerPath: 'conversationsApi',
  baseQuery,
  tagTypes: ['Conversations', 'ConversationDetail', 'Ratings', 'Accounts', 'Companies'],
  refetchOnMountOrArgChange: true,
  endpoints: (builder) => ({

    // List accounts (superadmin only)
    getAccounts: builder.query({
      query: () => '/api/v1/superadmin/accounts/',
      providesTags: ['Accounts']
    }),

    // List distinct company names for filtering
    getCompanies: builder.query({
      query: ({ accountId } = {}) => {
        const params = new URLSearchParams();
        if (accountId) params.append('account_id', accountId);
        return `/api/v1/admin/conversations/companies/?${params}`;
      },
      providesTags: ['Companies']
    }),

    // List conversations with filters
    getConversations: builder.query({
      query: ({ search, dateFrom, dateTo, preset, accountId, company, tab, page = 1, pageSize = 20 }) => {
        const params = new URLSearchParams({
          page: page.toString(),
          page_size: pageSize.toString(),
        });
        if (search) params.append('search', search);
        if (preset) params.append('preset', preset);
        if (dateFrom) params.append('date_from', dateFrom);
        if (dateTo) params.append('date_to', dateTo);
        if (accountId) params.append('account_id', accountId);
        if (company) params.append('company', company);
        if (tab) params.append('tab', tab);

        return `/api/v1/admin/conversations/?${params}`;
      },
      providesTags: ['Conversations']
    }),

    // Get conversation detail
    getConversationDetail: builder.query({
      query: (sessionId) => `/api/v1/admin/conversations/${sessionId}/`,
      providesTags: (result, error, sessionId) => [
        { type: 'ConversationDetail', id: sessionId }
      ]
    }),

    // Rate a message
    rateMessage: builder.mutation({
      query: ({ messageId, rating, feedback }) => ({
        url: `/api/v1/admin/conversations/messages/${messageId}/rate/`,
        method: 'POST',
        body: { rating, feedback }
      }),
      invalidatesTags: (result, error, { sessionId }) => [
        'Ratings',
        'ChatHistory',  // Invalidate chat history for normal chat interface
        { type: 'ConversationDetail', id: sessionId }
      ]
    }),

    // Analyze (or re-analyze) an existing bad rating on demand
    analyzeMessageRating: builder.mutation({
      query: ({ messageId }) => ({
        url: `/api/v1/admin/conversations/messages/${messageId}/analyze/`,
        method: 'POST'
      }),
      invalidatesTags: (result, error, { sessionId }) => [
        'Ratings',
        'ChatHistory',
        { type: 'ConversationDetail', id: sessionId }
      ]
    }),

    // Mark (or unmark) a bad-rating analysis as "not conform" -> flags it for team review
    markAnalysisReview: builder.mutation({
      query: ({ messageId, notConform = true, comment = '' }) => ({
        url: `/api/v1/admin/conversations/messages/${messageId}/review-flag/`,
        method: 'POST',
        body: { not_conform: notConform, comment }
      }),
      invalidatesTags: (result, error, { sessionId }) => [
        'Ratings',
        'ChatHistory',
        { type: 'ConversationDetail', id: sessionId }
      ]
    }),

    // Send admin message (auto-clears needs_attention flag)
    sendAdminMessage: builder.mutation({
      query: ({ sessionId, message }) => ({
        url: `/api/v1/admin/conversations/${sessionId}/send-message/`,
        method: 'POST',
        body: { message }
      }),
      invalidatesTags: (result, error, { sessionId }) => [
        'Conversations',
        { type: 'ConversationDetail', id: sessionId }
      ]
    }),

    // Dismiss attention flag
    dismissAttention: builder.mutation({
      query: (sessionId) => ({
        url: `/api/v1/admin/conversations/${sessionId}/dismiss-attention/`,
        method: 'POST'
      }),
      invalidatesTags: (result, error, sessionId) => [
        'Conversations',
        { type: 'ConversationDetail', id: sessionId }
      ]
    }),

  })
});

export const {
  useGetAccountsQuery,
  useGetCompaniesQuery,
  useGetConversationsQuery,
  useGetConversationDetailQuery,
  useRateMessageMutation,
  useAnalyzeMessageRatingMutation,
  useMarkAnalysisReviewMutation,
  useSendAdminMessageMutation,
  useDismissAttentionMutation,
} = conversationsApi;
